// Frontend version of platform association mappings for content types
// Defines which content types require platform associations and their default/available platforms

export interface ContentTypePlatformMapping {
  defaultPlatform?: string | null;  // Default platform key (null means user must select)
  availablePlatforms: string[];     // Available platform keys for this content type
  required: boolean;                // Whether platform association is required
}

export const CONTENT_TYPE_PLATFORM_MAP: Record<string, ContentTypePlatformMapping> = {
  'blog_post': { 
    defaultPlatform: 'rss',
    availablePlatforms: ['rss', 'wordpress', 'medium', 'linkedin_article'],
    required: true
  },
  'social_post': {
    defaultPlatform: null, // User must select platform
    availablePlatforms: ['linkedin', 'facebook', 'instagram', 'twitter', 'tiktok'],
    required: false // User can choose whether to schedule or not
  },
  'ad_group': {
    defaultPlatform: null,
    availablePlatforms: ['bing', 'facebook', 'google_ads', 'instagram', 'linkedin', 'twitter'],
    required: false
  },
  'email_newsletter': {
    defaultPlatform: 'email',
    availablePlatforms: ['email'],
    required: true
  },
  'landing_page': {
    // Landing pages don't have platform associations - they're hosted content
    defaultPlatform: null,
    availablePlatforms: [],
    required: false
  }
  // Other content types (press_release, email_sequence, etc.) don't have platform associations by default
}

/**
 * Get platform mapping for a content type
 */
export function getContentTypePlatformMapping(contentType: string): ContentTypePlatformMapping | null {
  return CONTENT_TYPE_PLATFORM_MAP[contentType] || null
}

/**
 * Check if a content type requires platform association
 */
export function contentTypeRequiresPlatform(contentType: string): boolean {
  const mapping = getContentTypePlatformMapping(contentType)
  return mapping?.required || false
}

/**
 * Get the default platform key for a content type
 */
export function getDefaultPlatformKey(contentType: string): string | null {
  const mapping = getContentTypePlatformMapping(contentType)
  return mapping?.defaultPlatform || null
}

/**
 * Get available platforms for a content type
 */
export function getAvailablePlatforms(contentType: string): string[] {
  const mapping = getContentTypePlatformMapping(contentType)
  return mapping?.availablePlatforms || []
}